// Vendor Portal — list of Published tenders that match the vendor's
// registered company categories. Each tender exposes a "Submit Documents"
// modal that lets the vendor upload up to 5 Technical PDFs (≤5 MB each)
// and one Financial PDF (≤2 MB) until the tender's deadline passes.
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, FileText, ExternalLink, IndianRupee, Calendar, Tag,
  Upload, Trash2, X, ClipboardCheck, AlertCircle, Building2,
} from "lucide-react";
import vendorApi from "@/lib/vendorApi";

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

export default function VendorTenders() {
  const [loading, setLoading] = useState(true);
  const [tenders, setTenders] = useState([]);
  const [vendorCats, setVendorCats] = useState([]);
  const [openId, setOpenId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await vendorApi.get("/vendor/tenders");
        setTenders(data.tenders || []);
        setVendorCats(data.vendor_categories || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto space-y-5" data-testid="vendor-tenders-page">
      <section className="bg-white border border-[#E9E2D2] rounded-2xl p-5 md:p-7">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#F26419] font-bold">Tenders</div>
            <h1 className="trust-serif text-2xl md:text-3xl mt-1 text-[#1A1A1A]">Relevant Tenders</h1>
            <p className="text-xs text-[#8a8478] mt-1">
              Live tenders matching your registered categories.
            </p>
          </div>
          {vendorCats.length > 0 && (
            <div className="max-w-md">
              <div className="text-[10px] uppercase tracking-widest text-[#8a8478]">Filtered by your categories</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {vendorCats.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#0D5C63]/10 text-[#0D5C63] px-2 py-0.5 rounded-full">
                    <Tag size={9} /> {c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {loading ? (
        <div className="text-sm text-[#0D5C63] flex items-center gap-2 justify-center py-16 bg-white border border-[#E9E2D2] rounded-2xl" data-testid="vendor-tenders-loading">
          <Loader2 size={14} className="animate-spin" /> Loading tenders…
        </div>
      ) : tenders.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-[#E9E2D2] rounded-2xl" data-testid="vendor-tenders-empty">
          <FileText size={28} className="mx-auto text-[#8a8478] mb-3" />
          <p className="text-sm text-[#5B5B5B]">
            {vendorCats.length === 0
              ? "No live tenders published right now."
              : "No live tenders match your registered categories yet."}
          </p>
          <p className="text-[11px] text-[#8a8478] mt-1">
            You will see new opportunities here as GS Mandal publishes them.
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="vendor-tenders-list">
          {tenders.map((t) => {
            const subs = Array.isArray(t.subcategories) ? t.subcategories : [];
            return (
              <li key={t.id} data-testid={`vendor-tender-${t.id}`} className="bg-white border border-[#E9E2D2] hover:border-[#0D5C63] rounded-2xl p-4 md:p-5 transition-colors">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#8a8478] font-mono">{t.reference_number || "—"} · {t.tender_type || "Tender"}</div>
                    <h3 className="text-base md:text-lg font-semibold text-[#1A1A1A] mt-0.5">{t.title}</h3>
                    {t.short_description && <p className="text-xs text-[#5B5B5B] mt-1 leading-relaxed line-clamp-2">{t.short_description}</p>}
                  </div>
                  {t.has_tender_fee && (
                    <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#c78e42]/15 text-[#8a5a1a] px-2.5 py-1 rounded-full">
                      <IndianRupee size={11} /> Fee ₹ {Number(t.tender_fee || 0).toLocaleString("en-IN")}
                    </div>
                  )}
                  {t.submission_mode === "Offline" && (
                    <div
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#0F0F10] text-[#c78e42] px-2.5 py-1 rounded-full"
                      data-testid={`vendor-tender-offline-${t.id}`}
                    >
                      <Building2 size={11} /> Offline · Hard-copy Only
                    </div>
                  )}
                </div>

                <div className="mt-3 grid sm:grid-cols-3 gap-2 text-[11px] text-[#5B5B5B]">
                  <div className="flex items-center gap-1.5"><Calendar size={12} className="text-[#8a8478]" /> Last submission: <b className="text-[#1A1A1A]">{fmt(t.last_submission_datetime)}</b></div>
                  <div className="flex items-center gap-1.5"><Calendar size={12} className="text-[#8a8478]" /> Bid opening: <b className="text-[#1A1A1A]">{fmt(t.bid_opening_date)}</b></div>
                  <div className="flex items-center gap-1.5"><Tag size={12} className="text-[#8a8478]" /> Category: <b className="text-[#1A1A1A]">{t.category || "—"}</b></div>
                </div>

                {subs.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {subs.map((s) => (
                      <span key={s} className="text-[10px] bg-[#F5EFE3] text-[#8a5a1a] px-2 py-0.5 rounded">
                        {s}
                      </span>
                    ))}
                  </div>
                )}

                {t.submission_mode === "Offline" && (
                  <div className="mt-3 bg-[#F5EFE3] border border-[#c78e42]/40 rounded-md px-3 py-2 text-[11px] text-[#8a5a1a]">
                    <b>Offline submission:</b> please deliver hard-copy Technical &amp; Financial envelopes at the GS Mandal office before the deadline.
                  </div>
                )}

                <div className="mt-4 flex items-center justify-end gap-2 flex-wrap">
                  {t.submission_mode !== "Offline" && (
                    <button
                      type="button"
                      onClick={() => setOpenId(t.id)}
                      data-testid={`vendor-tender-submit-${t.id}`}
                      className="inline-flex items-center gap-1.5 bg-[#0F0F10] hover:bg-[#1F1F20] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-full"
                    >
                      <Upload size={11} /> Submit Documents
                    </button>
                  )}
                  <Link
                    to={`/tenders/${t.id}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`vendor-tender-view-${t.id}`}
                    className="inline-flex items-center gap-1.5 bg-[#0D5C63] hover:bg-[#083E43] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-full"
                  >
                    View &amp; Apply <ExternalLink size={11} />
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {openId && (
        <SubmissionModal
          tenderId={openId}
          onClose={() => setOpenId("")}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------
// SubmissionModal — Technical (≤5 PDFs, 5 MB each) + Financial
// (single PDF, 2 MB) uploader for a specific tender.
// -------------------------------------------------------------
const TECH_MAX = 5 * 1024 * 1024;
const FIN_MAX = 2 * 1024 * 1024;

function SubmissionModal({ tenderId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await vendorApi.get(`/vendor/tenders/${tenderId}/submission`);
      setState(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load submission");
    } finally { setLoading(false); }
  }, [tenderId]);
  useEffect(() => { load(); }, [load]);

  const canSubmit = state?.can_submit;
  const sub = state?.submission || { technical_docs: [], financial_doc: null };
  const techCount = (sub.technical_docs || []).length;

  const uploadTech = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Only PDF files allowed");
    if (file.size > TECH_MAX) return toast.error("Technical document must be under 5 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await vendorApi.post(`/vendor/tenders/${tenderId}/technical`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState((s) => ({ ...s, submission: data.submission }));
      toast.success("Technical document uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  const deleteTech = async (docId) => {
    if (!window.confirm("Delete this technical document?")) return;
    setBusy(true);
    try {
      const { data } = await vendorApi.delete(`/vendor/tenders/${tenderId}/technical/${docId}`);
      setState((s) => ({ ...s, submission: data.submission }));
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };
  const uploadFin = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Only PDF files allowed");
    if (file.size > FIN_MAX) return toast.error("Financial document must be under 2 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await vendorApi.post(`/vendor/tenders/${tenderId}/financial`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState((s) => ({ ...s, submission: data.submission }));
      toast.success("Financial document uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  const deleteFin = async () => {
    if (!window.confirm("Delete the financial document?")) return;
    setBusy(true);
    try {
      const { data } = await vendorApi.delete(`/vendor/tenders/${tenderId}/financial`);
      setState((s) => ({ ...s, submission: data.submission }));
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="vendor-submission-modal"
    >
      <div
        className="bg-white shadow-2xl w-full max-w-2xl rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0F0F10] text-white px-6 py-4 flex items-start justify-between gap-3 border-b-4 border-[#c78e42]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#c78e42] font-semibold">Tender Submission</div>
            <h2 className="trust-serif text-xl leading-tight mt-1">
              {state?.tender?.title || "Loading…"}
            </h2>
            <div className="text-[11px] text-white/70 mt-1">
              Ref: {state?.tender?.reference_number || "—"}
              {state?.tender?.last_submission_datetime && <> · Closes {fmt(state.tender.last_submission_datetime)}</>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white p-1" aria-label="Close" data-testid="vendor-submission-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#6b655b]"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : err ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5" /> {err}
            </div>
          ) : !canSubmit ? (
            <div className="bg-[#F5EFE3] border border-[#c78e42]/40 rounded-md p-4 text-sm text-[#8a5a1a] flex items-start gap-2" data-testid="vendor-submission-blocked">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <div>
                <b>You cannot upload documents for this tender.</b>
                <div className="mt-1 text-[13px] text-[#5B5B5B]">{state?.blocked_reason || "Please contact GS Mandal."}</div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Technical documents */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#c78e42] font-bold">Technical Documents</div>
                    <div className="text-[11px] text-[#8a8478] mt-0.5">Up to 5 PDFs, max 5 MB each. ({techCount}/5)</div>
                  </div>
                  {techCount < 5 && (
                    <label className="inline-flex items-center gap-1.5 bg-[#0D5C63] hover:bg-[#083E43] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-full cursor-pointer disabled:opacity-40" data-testid="vendor-tech-add-label">
                      {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                      Add Technical PDF
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadTech(f); }}
                        className="hidden"
                        disabled={busy}
                        data-testid="vendor-tech-file"
                      />
                    </label>
                  )}
                </div>
                {(sub.technical_docs || []).length === 0 ? (
                  <div className="mt-3 bg-[#faf8f2] border border-dashed border-[#E9E2D2] rounded-md p-6 text-center text-xs text-[#8a8478]">
                    No technical documents uploaded yet.
                  </div>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {(sub.technical_docs || []).map((d, i) => (
                      <li key={d.id || i} className="bg-[#faf8f2] border border-[#E9E2D2] rounded-md px-3 py-2 flex items-center gap-3 text-xs" data-testid={`vendor-tech-row-${d.id}`}>
                        <FileText size={14} className="text-[#0D5C63] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium text-[#1A1A1A]">{d.filename || `Doc ${i + 1}`}</div>
                          <div className="text-[10px] text-[#8a8478]">{d.size ? `${Math.round(d.size / 1024)} KB` : ""} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : ""}</div>
                        </div>
                        <a href={d.url} target="_blank" rel="noreferrer" className="text-[#234333] hover:text-[#c78e42] font-semibold">
                          <ExternalLink size={13} />
                        </a>
                        <button
                          type="button"
                          onClick={() => deleteTech(d.id)}
                          disabled={busy}
                          className="text-red-700 hover:text-red-900 disabled:opacity-40"
                          data-testid={`vendor-tech-remove-${d.id}`}
                          aria-label="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Financial document */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#c78e42] font-bold">Financial Document</div>
                    <div className="text-[11px] text-[#8a8478] mt-0.5">Single PDF, max 2 MB. Uploading a new file replaces the previous one.</div>
                  </div>
                  {!sub.financial_doc && (
                    <label className="inline-flex items-center gap-1.5 bg-[#0D5C63] hover:bg-[#083E43] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-full cursor-pointer disabled:opacity-40" data-testid="vendor-fin-add-label">
                      {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                      Add Financial PDF
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadFin(f); }}
                        className="hidden"
                        disabled={busy}
                        data-testid="vendor-fin-file"
                      />
                    </label>
                  )}
                </div>
                {!sub.financial_doc ? (
                  <div className="mt-3 bg-[#faf8f2] border border-dashed border-[#E9E2D2] rounded-md p-6 text-center text-xs text-[#8a8478]">
                    Financial document not uploaded yet.
                  </div>
                ) : (
                  <div className="mt-3 bg-[#faf8f2] border border-[#E9E2D2] rounded-md px-3 py-2 flex items-center gap-3 text-xs" data-testid="vendor-fin-row">
                    <ClipboardCheck size={14} className="text-[#0f7a45] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium text-[#1A1A1A]">{sub.financial_doc.filename || "Financial.pdf"}</div>
                      <div className="text-[10px] text-[#8a8478]">{sub.financial_doc.size ? `${Math.round(sub.financial_doc.size / 1024)} KB` : ""} · {sub.financial_doc.uploaded_at ? new Date(sub.financial_doc.uploaded_at).toLocaleString() : ""}</div>
                    </div>
                    <a href={sub.financial_doc.url} target="_blank" rel="noreferrer" className="text-[#234333] hover:text-[#c78e42] font-semibold">
                      <ExternalLink size={13} />
                    </a>
                    <label className="inline-flex items-center gap-1 text-[#234333] hover:text-[#c78e42] font-semibold cursor-pointer" title="Replace">
                      <Upload size={13} />
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadFin(f); }}
                        className="hidden"
                        disabled={busy}
                        data-testid="vendor-fin-replace"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={deleteFin}
                      disabled={busy}
                      className="text-red-700 hover:text-red-900 disabled:opacity-40"
                      data-testid="vendor-fin-remove"
                      aria-label="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
